/**
 * src/pages/admin/AdminRatingClubs.jsx
 *
 * Scrimly Ratings club directory (features/club-ratings, Wireframe screen
 * 13). List, add, edit, merge, mark inactive, hold and release. Every write
 * goes through the founder-gated ratingsAdminClubs callable, which validates
 * slugs (reserved list, uniqueness), keeps old slugs for redirects, and
 * writes adminAudit.
 *
 * English only (admin path, BA §6).
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { usePlatformClaims } from '@/hooks/usePlatformClaims';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const fns = () => getFunctions(undefined, 'us-central1');
const call = async (data) => (await httpsCallable(fns(), 'ratingsAdminClubs')(data)).data;

const COUNTIES = ['riverside', 'san-bernardino', 'los-angeles', 'orange', 'san-diego', 'ventura'];
const AGES = ['U6', 'U7', 'U8', 'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U18', 'U19'];
const STATUS_CLS = {
  active: 'bg-lime-100 text-lime-800 border-lime-200',
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  inactive: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  merged: 'bg-zinc-100 text-zinc-500 border-zinc-200',
  rejected: 'bg-zinc-100 text-zinc-500 border-zinc-200',
};

// Program facts (features/club-cost-and-level/, Arch A-5). Codes must match
// core.LEVELS and core.TEAMS in functions/modules/clubRatings/core.js.
const LEVELS = [
  ['ecnl_b', 'ECNL Boys'], ['ecnl_g', 'ECNL Girls'],
  ['ecnlrl_b', 'ECNL RL Boys'], ['ecnlrl_g', 'ECNL RL Girls'],
  ['ga', 'Girls Academy'], ['mlsnext', 'MLS NEXT'],
];
const TEAMS = [['both', 'Boys and girls'], ['boys', 'Boys only'], ['girls', 'Girls only'], ['', 'Not known']];

const EMPTY = {
  name: '', city: '', county: '', website: '', leagues: '', ageMin: '', ageMax: '', slug: '',
  levels: [], teams: '', programsCheckedAt: '', programsSourceUrl: '',
};

// Stored age lists are usually the full list (U8, U9, ... U19), so the
// youngest and oldest come from AGES order, not the first two entries.
function ageBounds(list) {
  const idx = (list || []).map((a) => AGES.indexOf(a)).filter((i) => i >= 0).sort((a, b) => a - b);
  return idx.length ? [AGES[idx[0]], AGES[idx[idx.length - 1]]] : ['', ''];
}

function toForm(c) {
  const [ageMin, ageMax] = ageBounds(c.ageGroups);
  return {
    name: c.name || '',
    city: c.city || '',
    county: c.county || '',
    website: c.website || '',
    leagues: (c.leagues || []).join(', '),
    ageMin,
    ageMax,
    slug: c.slug || '',
    levels: c.levels || [],
    teams: c.teams || '',
    programsCheckedAt: c.programsCheckedAt || '',
    programsSourceUrl: c.programsSourceUrl || '',
  };
}

// Keeps the stored age list as it is when the youngest and oldest didn't
// change, so saving an unrelated field never rewrites a club's ages.
function fromForm(f, original) {
  const [origMin, origMax] = ageBounds(original?.ageGroups);
  let ageGroups = [];
  if (f.ageMin && f.ageMax) {
    ageGroups = f.ageMin === origMin && f.ageMax === origMax
      ? original.ageGroups
      : AGES.slice(AGES.indexOf(f.ageMin), AGES.indexOf(f.ageMax) + 1);
  }
  return {
    name: f.name.trim(),
    city: f.city.trim(),
    county: f.county,
    website: f.website.trim(),
    leagues: f.leagues.split(',').map((s) => s.trim()).filter(Boolean),
    ageGroups,
    slug: f.slug.trim() || undefined,
    levels: f.levels,
    teams: f.teams || null,
    programsCheckedAt: f.programsCheckedAt || '',
    programsSourceUrl: f.programsSourceUrl || '',
  };
}

export default function AdminRatingClubs() {
  const { isFounder } = usePlatformClaims();
  const blocked = !isFounder;

  const [clubs, setClubs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [filter, setFilter] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState(null); // clubId | 'new'
  const [form, setForm] = useState(EMPTY);
  const [mergeFrom, setMergeFrom] = useState(null);
  const [mergeInto, setMergeInto] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await call({ action: 'list' });
      setClubs(res.clubs || []);
    } catch (e) {
      setError(e?.message || 'Could not load the directory.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (!blocked) load(); }, [blocked, load]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (clubs || [])
      .filter((c) => showAll || c.status === 'active' || c.status === 'inactive')
      .filter((c) => !q || c.name.toLowerCase().includes(q) || c.city.toLowerCase().includes(q));
  }, [clubs, filter, showAll]);

  const active = (clubs || []).filter((c) => c.status === 'active');

  async function run(data, confirmText, okText) {
    if (confirmText && !window.confirm(confirmText)) return false;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await call(data);
      setNotice(okText ? okText(res) : 'Saved.');
      await load();
      return true;
    } catch (e) {
      setError(e?.message || 'That action failed.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function save(e) {
    e.preventDefault();
    const fields = fromForm(form, editing === 'new' ? null : clubs.find((c) => c.clubId === editing));
    const ok = editing === 'new'
      ? await run({ action: 'add', ...fields }, null, () => 'Club added.')
      : await run({ action: 'edit', clubId: editing, ...fields }, null, () => 'Club updated.');
    if (ok) { setEditing(null); setForm(EMPTY); }
  }

  async function merge() {
    const from = clubs.find((c) => c.clubId === mergeFrom);
    const into = clubs.find((c) => c.clubId === mergeInto);
    const ok = await run(
      { action: 'merge', clubId: mergeFrom, intoClubId: mergeInto },
      `Merge "${from?.name}" into "${into?.name}"?\n\n${from?.publishedCount || 0} published rating(s) move. Old links redirect. This can't be undone from here.`,
      (res) => `Merged: ${res.moved} moved, ${res.dropped} duplicate(s) removed.`,
    );
    if (ok) { setMergeFrom(null); setMergeInto(''); }
  }

  if (blocked) {
    return <div className="p-6 text-sm text-zinc-600">Founder access only.</div>;
  }

  const input = 'mt-1 w-full rounded-lg border border-zinc-300 px-2 py-1.5 text-sm';

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Club directory</h1>
          <p className="text-sm text-zinc-500">
            Scrimly Ratings · <Link to="/admin/ratings" className="text-lime-700 hover:underline">Moderation queue</Link>
          </p>
        </div>
        <button onClick={load} disabled={loading} className="p-2 rounded-lg hover:bg-zinc-100 disabled:opacity-40" aria-label="Refresh">
          <ArrowPathIcon className={`w-5 h-5 text-zinc-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <ExclamationTriangleIcon className="w-5 h-5 flex-none" /><span>{error}</span>
        </div>
      )}
      {notice && <div className="mb-4 rounded-lg border border-lime-200 bg-lime-50 p-3 text-sm text-lime-900">{notice}</div>}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="search" placeholder="Filter by name or city" value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-lg border border-zinc-300 px-3 py-1.5 text-sm w-full sm:w-64"
        />
        <label className="text-sm text-zinc-600 flex items-center gap-1.5">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Show pending, merged and rejected
        </label>
        <button
          type="button"
          onClick={() => { setEditing('new'); setForm(EMPTY); }}
          className="ml-auto px-3 py-1.5 rounded-lg text-sm font-semibold bg-lime-400 text-zinc-900 hover:bg-lime-300"
        >
          Add club
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="mb-4 rounded-xl border border-zinc-200 bg-zinc-50 p-4 grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2 font-semibold text-zinc-900">{editing === 'new' ? 'Add club' : 'Edit club'}</div>
          <label className="text-xs text-zinc-500">Name<input required className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label className="text-xs text-zinc-500">City<input required className={input} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></label>
          <label className="text-xs text-zinc-500">County
            <select className={input} value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })}>
              <option value="">Choose…</option>
              {COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-xs text-zinc-500">Website (https only)<input className={input} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" /></label>
          <label className="text-xs text-zinc-500">Leagues (comma separated)<input className={input} value={form.leagues} onChange={(e) => setForm({ ...form, leagues: e.target.value })} /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-zinc-500">Youngest
              <select className={input} value={form.ageMin} onChange={(e) => setForm({ ...form, ageMin: e.target.value })}>
                <option value="">—</option>{AGES.map((a) => <option key={a}>{a}</option>)}
              </select>
            </label>
            <label className="text-xs text-zinc-500">Oldest
              <select className={input} value={form.ageMax} onChange={(e) => setForm({ ...form, ageMax: e.target.value })}>
                <option value="">—</option>{AGES.map((a) => <option key={a}>{a}</option>)}
              </select>
            </label>
          </div>
          <fieldset className="sm:col-span-2 rounded-lg border border-zinc-200 bg-white p-3">
            <legend className="px-1 text-xs font-semibold text-zinc-700">Programs (public facts, from league lists and the club site)</legend>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1">
              {LEVELS.map(([code, label]) => (
                <label key={code} className="text-sm text-zinc-700 flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={form.levels.includes(code)}
                    onChange={(e) => setForm({
                      ...form,
                      levels: e.target.checked ? [...form.levels, code] : form.levels.filter((l) => l !== code),
                    })}
                  />
                  {label}
                </label>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {TEAMS.map(([code, label]) => (
                <label key={code || 'unknown'} className="text-sm text-zinc-700 flex items-center gap-1.5">
                  <input type="radio" name="teams" checked={form.teams === code} onChange={() => setForm({ ...form, teams: code })} />
                  {label}
                </label>
              ))}
            </div>
            <label className="mt-2 block text-xs text-zinc-500">Source checked
              <input type="date" className={input} value={form.programsCheckedAt} onChange={(e) => setForm({ ...form, programsCheckedAt: e.target.value })} />
            </label>
            <label className="mt-2 block text-xs text-zinc-500">Source page (https; blank = club website). Levels and teams show publicly only with a checked date.
              <input type="url" className={input} value={form.programsSourceUrl} onChange={(e) => setForm({ ...form, programsSourceUrl: e.target.value })} placeholder="https://" />
            </label>
          </fieldset>
          <label className="text-xs text-zinc-500 sm:col-span-2">Slug (blank = from name). Changing it keeps the old one as a redirect.
            <input className={input} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
          </label>
          <div className="sm:col-span-2 flex gap-2">
            <button type="submit" disabled={busy} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-lime-400 text-zinc-900 hover:bg-lime-300 disabled:opacity-40">{busy ? '…' : 'Save'}</button>
            <button type="button" onClick={() => setEditing(null)} className="px-3 py-1.5 rounded-lg text-sm border border-zinc-200 bg-white">Cancel</button>
          </div>
        </form>
      )}

      {mergeFrom && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-wrap items-end gap-2">
          <div className="text-sm text-amber-900 w-full">
            Merge <strong>{clubs.find((c) => c.clubId === mergeFrom)?.name}</strong> into:
          </div>
          <select value={mergeInto} onChange={(e) => setMergeInto(e.target.value)} className="rounded-lg border border-zinc-300 px-2 py-1.5 text-sm">
            <option value="">Choose a club…</option>
            {active.filter((c) => c.clubId !== mergeFrom).map((c) => <option key={c.clubId} value={c.clubId}>{c.name} · {c.city}</option>)}
          </select>
          <button type="button" disabled={!mergeInto || busy} onClick={merge} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-zinc-900 text-white disabled:opacity-40">Merge</button>
          <button type="button" onClick={() => setMergeFrom(null)} className="px-3 py-1.5 rounded-lg text-sm border border-zinc-200 bg-white">Cancel</button>
        </div>
      )}

      {loading && !clubs && <div className="rounded-lg border border-zinc-200 p-6 text-sm text-zinc-500">Loading…</div>}

      {clubs && (
        <div className="rounded-xl border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="text-left p-3">Club</th>
                <th className="text-left p-3">County</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Published</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {shown.length === 0 && (
                <tr><td colSpan={5} className="p-4 text-zinc-500">{clubs.length ? 'No clubs match.' : 'No clubs yet. Add the first one.'}</td></tr>
              )}
              {shown.map((c) => (
                <tr key={c.clubId}>
                  <td className="p-3">
                    <div className="font-medium text-zinc-900">{c.name}</div>
                    <div className="text-xs text-zinc-500">{c.city}{c.slug ? ` · /${c.slug}` : ''}</div>
                  </td>
                  <td className="p-3 text-zinc-600">{c.county || '—'}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_CLS[c.status] || STATUS_CLS.inactive}`}>{c.status}</span>
                    {c.flagged && <span className="ml-1 text-xs px-2 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-800">flagged</span>}
                  </td>
                  <td className="p-3 text-right tabular-nums">{c.publishedCount}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {(c.status === 'active' || c.status === 'inactive') && (
                      <>
                        <button type="button" className="text-lime-700 hover:underline mr-3" onClick={() => { setEditing(c.clubId); setForm(toForm(c)); }}>Edit</button>
                        <button type="button" className="text-zinc-600 hover:underline mr-3" onClick={() => { setMergeFrom(c.clubId); setMergeInto(''); }}>Merge…</button>
                      </>
                    )}
                    {c.status === 'active' && (
                      <>
                        <button type="button" className="text-zinc-600 hover:underline mr-3" disabled={busy}
                          onClick={() => run({ action: 'hold', clubId: c.clubId }, `Hold all waiting ratings for ${c.name}?`, (r) => `Held ${r.count}.`)}>Hold</button>
                        <button type="button" className="text-zinc-600 hover:underline mr-3" disabled={busy}
                          onClick={() => run({ action: 'release', clubId: c.clubId }, null, (r) => `Released ${r.count}.`)}>Release</button>
                        <button type="button" className="text-red-700 hover:underline" disabled={busy}
                          onClick={() => run({ action: 'set_inactive', clubId: c.clubId }, `Mark ${c.name} as no longer active? Its page stays up with a banner and new ratings stop.`)}>Inactive</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
